import isSSR from '@calm/web-common/dist/isSSR';
import { RefObject, useEffect, useLayoutEffect, useRef } from 'react';

export type ResizeObserverTarget =
    | RefObject<Element | null>
    | Element
    | null
    | undefined;

export interface UseResizeObserverOptions {
    /** Skip observing entirely, e.g. for a prop-gated caller. Defaults to true. */
    isEnabled?: boolean;
}

// Measuring before paint keeps callers from rendering a frame against a
// measured zero. useLayoutEffect warns during SSR, where it never runs anyway,
// so resolve the choice once at module scope to keep hook order stable.
const useIsomorphicLayoutEffect = isSSR() ? useEffect : useLayoutEffect;

function resolveTarget(target: ResizeObserverTarget): Element | null {
    if (!target) {
        return null;
    }
    return 'current' in target ? target.current : target;
}

/**
 * Runs `onResize` once before paint and again whenever `target` resizes.
 *
 * Owns the observer lifecycle — initial synchronous measurement, `observe`, and
 * `disconnect` on unmount — but leaves the measuring to the caller, so callers
 * deriving something other than a size (an overflow flag, a descendant's
 * position) share the same wiring. For a plain `{width, height}`, use
 * `useContainerSize`, which is built on this.
 *
 * `target` may be a ref object or the element itself. A ref object is resolved
 * only when `target` or `isEnabled` changes, and a ref's identity never
 * changes, so an element that mounts after the first run (a conditional
 * render) is never picked up - hold such an element in state and pass the
 * element itself, so its arrival re-runs the subscription.
 *
 * To watch more than one element, call the hook once per element with the same
 * callback. Passing the element as an array is deliberately not supported: the
 * target is an effect dependency, and a fresh array each render would
 * re-subscribe the observer on every render.
 *
 * @param target Element to observe, as a ref object or the element itself.
 * @param onResize Called with the observed element, before paint and on resize.
 * @param options See {@link UseResizeObserverOptions}.
 */
export function useResizeObserver(
    target: ResizeObserverTarget,
    onResize: (element: Element) => void,
    { isEnabled = true }: UseResizeObserverOptions = {}
): void {
    // Store the latest callback in a ref so an unmemoised one does not
    // re-subscribe the observer on every render.
    const onResizeRef = useRef(onResize);
    onResizeRef.current = onResize;

    useIsomorphicLayoutEffect(() => {
        const element = isEnabled ? resolveTarget(target) : null;
        if (!element) {
            return;
        }

        const handleResize = () => onResizeRef.current(element);
        handleResize();

        const observer = new ResizeObserver(handleResize);
        observer.observe(element);
        return () => observer.disconnect();
    }, [target, isEnabled]);
}
